import { useMemo } from "react";
import { useQuery } from "react-query";
import { useRegressionApi } from "@/v2/entities/regression/Regression.api";
import type {
  RegressionDecision,
  RegressionTestResultDetail,
} from "@/v2/entities/regression/Regression.model";

export interface RegressionTestComparisonRow {
  regressionSetItemId: string;
  question: string;
  expectedAnswer: string;
  test1Answer: string | null;
  test2Answer: string | null;
  test1Decision: string | null;
  test2Decision: string | null;
  test1Similarity: number | null;
  test2Similarity: number | null;
}

function formatDecisionLabel(decision: RegressionDecision | null): string | null {
  if (decision === null) return null;
  if (decision === "escalate") return "Escalate";
  return "None";
}

function buildResultByItemId(
  details: RegressionTestResultDetail[] | undefined
): Map<string, RegressionTestResultDetail> {
  const map = new Map<string, RegressionTestResultDetail>();
  details?.forEach((row) => {
    map.set(row.regressionSetItemId, row);
  });
  return map;
}

export function useRegressionTestComparisonRows(
  regressionSetId: string | null,
  test1Id: string | null,
  test2Id: string | null
) {
  const { getRegressionSet, getRegressionTestResults, getRegressionTestInfo } =
    useRegressionApi();

  const { data: regressionSet, isLoading: regressionSetLoading } = useQuery({
    queryKey: ["regression-set", regressionSetId],
    queryFn: () => getRegressionSet(regressionSetId ?? ""),
    enabled: Boolean(regressionSetId),
  });

  const { data: results1, isLoading: results1Loading } = useQuery({
    queryKey: ["regression-test-results", test1Id],
    queryFn: () => getRegressionTestResults(test1Id ?? ""),
    enabled: Boolean(test1Id),
  });

  const { data: results2, isLoading: results2Loading } = useQuery({
    queryKey: ["regression-test-results", test2Id],
    queryFn: () => getRegressionTestResults(test2Id ?? ""),
    enabled: Boolean(test2Id),
  });

  const { data: info1, isLoading: info1Loading } = useQuery({
    queryKey: ["regression-test-info", test1Id],
    queryFn: () => getRegressionTestInfo(test1Id ?? ""),
    enabled: Boolean(test1Id),
  });

  const { data: info2, isLoading: info2Loading } = useQuery({
    queryKey: ["regression-test-info", test2Id],
    queryFn: () => getRegressionTestInfo(test2Id ?? ""),
    enabled: Boolean(test2Id),
  });

  const rows = useMemo((): RegressionTestComparisonRow[] => {
    if (!regressionSet?.items?.length) return [];

    const byItem1 = buildResultByItemId(results1);
    const byItem2 = buildResultByItemId(results2);

    return regressionSet.items.map((item) => {
      const r1 = byItem1.get(item.id);
      const r2 = byItem2.get(item.id);

      return {
        regressionSetItemId: item.id,
        question: item.question,
        expectedAnswer: item.expectedAnswer,
        test1Answer: r1?.aiResponse ?? null,
        test2Answer: r2?.aiResponse ?? null,
        test1Decision: formatDecisionLabel(r1?.decision ?? null),
        test2Decision: formatDecisionLabel(r2?.decision ?? null),
        test1Similarity:
          typeof r1?.similarity === "number" ? r1.similarity : null,
        test2Similarity:
          typeof r2?.similarity === "number" ? r2.similarity : null,
      };
    });
  }, [regressionSet, results1, results2]);

  const isLoading =
    regressionSetLoading ||
    results1Loading ||
    results2Loading ||
    info1Loading ||
    info2Loading;

  return {
    rows,
    isLoading,
    test1RunMethod: info1?.runMethod,
    test2RunMethod: info2?.runMethod,
  };
}
